import { useCallback, useState } from 'react';
import { toast } from '@clarix/ui';
import { quickMeetingApi } from './api';
import { clearLocalParts, downloadLocalAudio, sidecars, type SessionSidecar } from './browser';
import { QM_COPY, START_ERROR_COPY } from './copy';
import { errorCode } from './format';
import { isOrphanAge } from './QuickMeetingContext';
import { keepsLocalCopy, saveEndedToast, saveRecording } from './useQuickMeetingRecorder';

/**
 * Actions on the caller's own server-side recordings listed under the mic: save or discard an
 * unfinished one (draining this device's parts when it still has them), process a salvaged one,
 * retry a failed stage. Every one of them re-reads the status afterwards.
 */
export function useRecordingRowActions(slug: string | null, scope: string | null, refresh: () => void) {
  const [pendingId, setPendingId] = useState<string | null>(null);

  const run = useCallback(
    async (id: string, action: () => Promise<void>) => {
      setPendingId(id);
      try {
        await action();
      } catch (err) {
        toast.error(START_ERROR_COPY[errorCode(err).code] ?? QM_COPY.actionFailed);
      } finally {
        setPendingId(null);
        refresh();
      }
    },
    [refresh],
  );

  /** "Download from this device": the take's audio as a file; an entry with none left is dropped. */
  const downloadLocal = useCallback(
    async (entry: SessionSidecar) => {
      if (!scope) return;
      if (await downloadLocalAudio(scope, entry)) return;
      toast.info(QM_COPY.nothingOnDevice);
      sidecars.remove(scope, entry.recordingId);
      refresh();
    },
    [refresh, scope],
  );

  /** Deletes a take's audio from this device only (asked first); the server's copy is untouched. */
  const deleteLocal = useCallback(
    (entry: SessionSidecar) =>
      run(entry.recordingId, async () => {
        if (!scope) return;
        await clearLocalParts(scope, entry.clientSessionId);
        sidecars.remove(scope, entry.recordingId);
      }),
    [run, scope],
  );

  /**
   * Uploads what this device still holds for a take no tab drives (closed, crashed, paused), then
   * completes. `recordedSeconds` is the row's last heartbeat: the time actually captured, never the gap.
   */
  const save = useCallback(
    (id: string, recordedSeconds?: number | null) =>
      run(id, async () => {
        if (!slug || !scope) return;
        const entry = sidecars.find(scope, id);
        const result = await saveRecording(
          slug,
          scope,
          entry ?? { recordingId: id, clientSessionId: id, uploadId: '' },
          recordedSeconds ?? undefined,
          !!entry,
          undefined,
          (uploadId) => sidecars.update(scope, id, { uploadId }),
        );
        if (result.kind === 'failed') {
          if (result.wait) {
            toast.info(result.wait === 'unreachable' ? QM_COPY.uploadStuck : QM_COPY.offlineTryAgain);
            return;
          }
          throw new Error('save failed');
        }
        // The session record indexes this device's parts: it goes only once they are gone.
        if (entry && keepsLocalCopy(result)) sidecars.markEnded(scope, id);
        else sidecars.remove(scope, id);
        if (result.kind === 'saved') toast.success({ title: QM_COPY.savedTitle, description: QM_COPY.savedBody });
        else saveEndedToast(result, entry ? () => void downloadLocal(entry) : undefined);
      }),
    [downloadLocal, run, scope, slug],
  );

  const discard = useCallback(
    (id: string) =>
      run(id, async () => {
        if (!slug || !scope) return;
        // A take that is still `recording` is discarded only if a fresh read shows no tab drives it.
        const { active } = await quickMeetingApi.status(slug);
        if (active?.id === id && !isOrphanAge(active.heartbeat_age_seconds, 0)) {
          toast.info(QM_COPY.stillRunning);
          return;
        }
        await quickMeetingApi.abort(slug, id);
        const entry = sidecars.find(scope, id);
        if (entry) await clearLocalParts(scope, entry.clientSessionId);
        sidecars.remove(scope, id);
      }),
    [run, scope, slug],
  );

  const process = useCallback(
    (id: string) =>
      run(id, async () => {
        if (slug) await quickMeetingApi.process(slug, id);
      }),
    [run, slug],
  );

  const retryProcessing = useCallback(
    (id: string) =>
      run(id, async () => {
        if (slug) await quickMeetingApi.retry(slug, id);
      }),
    [run, slug],
  );

  return { pendingId, save, discard, process, retryProcessing, downloadLocal, deleteLocal };
}
